import { useCallback, useRef, useState } from "react";

import { useAppDispatch } from "@/redux/hooks";

import { People } from "@/pages/Private/pages/Apollo/schema/get-people";

import { isCreditsRefusalMessage } from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";
import { DeepResearchRun } from "@/pages/Private/redux/deepResearch/schema";
import { updateDeepResearchResponseInRecords } from "@/pages/Private/redux/profileTracker/profileTracker.slice";
import { updateDeepResearchResponseInInbox } from "@/pages/Private/redux/list/list.slice";
import { updateDeepResearchResponseInContacts } from "@/pages/Private/redux/contact/contact.slice";

import {
	DeepResearchFailure,
	DeepResearchRunView,
	isSupersededBy,
	PendingRun,
	seedFailures,
	seedPendingRuns,
} from "./deepResearchRunState";

type DeepResearchResponseData = NonNullable<People["deepResearchResponse"]>["response"];

/**
 * Shape of a `deep-research-result` socket payload. Shared by the web socket
 * provider and the Chrome-extension bridge provider so both apply the result
 * (and clear pending / surface errors) identically.
 */
export interface DeepResearchResultUpdate {
	deepResearchResponseId: number | null;
	prospectId: number;
	deepResearchPromptId: number | null;
	companyId: number;
	trackerOrganizationId?: number;
	status: "completed" | "failed";
	response?: DeepResearchResponseData;
	fitAssessment?: string | null;
	fitReason?: string | null;
	error?: string;
}

export interface DeepResearchResultController {
	isPending: (prospectId?: number) => boolean;
	/** Queued or running, and since when; null when no run is in flight (PREC-4052). */
	getRun: (prospectId?: number) => DeepResearchRunView | null;
	/** Mark a run as scheduled; `baselineResponseId` is the research shown at that moment. */
	startPending: (prospectId?: number, baselineResponseId?: number | null) => void;
	/**
	 * Hand a run the research the lead currently carries (e.g. from a detail
	 * refetch). Clears pending when that research is not the one the run started
	 * from — the result landed without its socket event (PREC-4024).
	 */
	settlePending: (prospectId?: number, responseId?: number | null) => void;
	/** The lead's last run failed, and whether a retry can succeed (PREC-4052). */
	getFailure: (prospectId?: number) => DeepResearchFailure | null;
	clearError: (prospectId?: number) => void;
	/**
	 * Take the server's answer for `askedProspectIds`, asked at `askedAt` (epoch ms): its queued
	 * and running runs, its failures, and the end of every run it no longer reports (PREC-4052).
	 */
	seedRuns: (runs: DeepResearchRun[], askedProspectIds: number[], askedAt: number) => void;
	/** Apply a `deep-research-result` payload: clear pending, then update the
	 *  slice records on success or record the failure. */
	applyDeepResearchResult: (data: DeepResearchResultUpdate) => void;
}

const without = <V>(map: Map<number, V>, prospectId: number): Map<number, V> => {
	const next = new Map(map);

	next.delete(prospectId);

	return next;
};

/**
 * Owns the per-prospect run and failure state for deep research plus the logic
 * that applies a result payload to the Redux slices. Kept transport-agnostic so
 * the web socket provider (`DeepResearchSocketProvider`) and the Chrome
 * extension's background-bridge provider can share the exact same behaviour —
 * only the source of the `deep-research-result` event differs.
 *
 * The state lives in memory only. The server's run rows are the record (PREC-4052): a page
 * asks for them (`useSeedDeepResearchRuns`) instead of keeping a stamp in page storage, which
 * the extension sandboxes and which expired after ten minutes while a research may queue for
 * thirty.
 */
export const useDeepResearchResultController = (): DeepResearchResultController => {
	const dispatch = useAppDispatch();
	const [pendingRuns, setPendingRuns] = useState<Map<number, PendingRun>>(new Map());
	const [failures, setFailures] = useState<Map<number, DeepResearchFailure>>(new Map());
	const dismissedAtRef = useRef<Map<number, number>>(new Map());

	const isPending = useCallback(
		(prospectId?: number): boolean => !!prospectId && pendingRuns.has(prospectId),
		[pendingRuns]
	);

	const getRun = useCallback(
		(prospectId?: number): DeepResearchRunView | null => {
			const run = prospectId ? pendingRuns.get(prospectId) : undefined;

			return run ? { phase: run.phase, since: run.since } : null;
		},
		[pendingRuns]
	);

	const startPending = useCallback(
		(prospectId?: number, baselineResponseId?: number | null): void => {
			if (!prospectId) {
				return;
			}

			const run: PendingRun = {
				phase: "queued",
				since: Date.now(),
				baselineResponseId: baselineResponseId ?? null,
				local: true,
			};

			setPendingRuns((prev) => new Map(prev).set(prospectId, run));
			setFailures((prev) => without(prev, prospectId));
		},
		[]
	);

	const settlePending = useCallback(
		(prospectId?: number, responseId?: number | null): void => {
			if (!prospectId || responseId == null) {
				return;
			}

			const run = pendingRuns.get(prospectId);

			if (run && isSupersededBy(run, responseId)) {
				setPendingRuns((prev) => without(prev, prospectId));
			}
		},
		[pendingRuns]
	);

	const getFailure = useCallback(
		(prospectId?: number): DeepResearchFailure | null =>
			(prospectId ? failures.get(prospectId) : undefined) ?? null,
		[failures]
	);

	const clearError = useCallback(
		(prospectId?: number): void => {
			if (!prospectId) {
				return;
			}

			dismissedAtRef.current.set(prospectId, failures.get(prospectId)?.failedAt ?? Date.now());
			setFailures((prev) => without(prev, prospectId));
		},
		[failures]
	);

	const seedRuns = useCallback(
		(runs: DeepResearchRun[], askedProspectIds: number[], askedAt: number): void => {
			const seed = { runs, asked: askedProspectIds, askedAt, dismissedAt: dismissedAtRef.current };
			const nextPending = seedPendingRuns(pendingRuns, seed);

			setPendingRuns(nextPending);
			setFailures((prev) => seedFailures(prev, nextPending, seed));
		},
		[pendingRuns]
	);

	const applyDeepResearchResult = useCallback(
		(data: DeepResearchResultUpdate): void => {
			setPendingRuns((prev) => without(prev, data.prospectId));

			if (data.status === "completed") {
				setFailures((prev) => without(prev, data.prospectId));

				// Update the deep research response in-place across every slice that
				// may hold this prospect (records / inbox / contacts).
				if (data.deepResearchResponseId && data.response) {
					const deepResearchResponsePayload = {
						prospectId: data.prospectId,
						deepResearchResponseId: data.deepResearchResponseId,
						response: data.response,
						fitAssessment: data.fitAssessment ?? null,
						fitReason: data.fitReason ?? null,
					};

					dispatch(updateDeepResearchResponseInRecords(deepResearchResponsePayload));
					dispatch(updateDeepResearchResponseInInbox(deepResearchResponsePayload));
					dispatch(updateDeepResearchResponseInContacts(deepResearchResponsePayload));
				}

				return;
			}

			const failure: DeepResearchFailure = {
				kind: isCreditsRefusalMessage(data.error) ? "credits" : "failed",
				learnedAt: Date.now(),
			};

			setFailures((prev) => new Map(prev).set(data.prospectId, failure));
		},
		[dispatch]
	);

	return {
		isPending,
		getRun,
		startPending,
		settlePending,
		getFailure,
		clearError,
		seedRuns,
		applyDeepResearchResult,
	};
};
